import { Bot, Check, Copy, History, Pencil, Search, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatDate } from "../api";
import { useAuth } from "../auth";
import { tierLabel } from "../components/PromptPicker";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "../components/Ui";
import FilterPill from "../components/FilterPill";
import { useShowMore } from "../components/Workflow";
import { useApi } from "../hooks";
import { noDash } from "../insights";

type Prompt = {
  id: number;
  prompt_name: string;
  prompt_body: string;
  model_tier: string;
  updated_at: string;
  category_name: string;
  category_slug: string;
  color_hex: string | null;
  version_count: number | string;
  satisfaction_pct: number | string | null;
  vote_count?: number | string | null;
};

/** Fewer votes than this say nothing about a prompt, so no percentage is shown. */
const MIN_VOTES = 3;

/** "75% helpful, 4 votes in the last 30 days", or null while there are too few votes. */
function helpfulLine(p: Prompt): string | null {
  const votes = Number(p.vote_count ?? 0);
  if (p.satisfaction_pct == null || !Number.isFinite(votes) || votes < MIN_VOTES) return null;
  return `${Number(p.satisfaction_pct)}% helpful, ${votes} votes in the last 30 days`;
}

const earlierVersions = (n: number) => (n > 0 ? `${n} earlier ${n === 1 ? "version" : "versions"}` : "");
type Category = { id: number; name: string; slug: string; color_hex: string | null };
type Version = { id: number; version_number: number; prompt_body: string; model_tier: string; created_at: string; change_reason: string | null };

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    document.execCommand("copy");
    area.remove();
  }
}

/** Copy with a short "Copied" confirmation on the button itself. */
function CopyButton({ text, label = "Copy", compact }: { text: string; label?: string; compact?: boolean }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className={`pl-copy${done ? " done" : ""}${compact ? " compact" : ""}`}
      onClick={async (e) => {
        e.stopPropagation();
        await copyText(text);
        setDone(true);
        window.setTimeout(() => setDone(false), 1500);
      }}
      aria-label={done ? "Copied to clipboard" : `${label} prompt`}
      title={done ? "Copied" : "Copy prompt to clipboard"}
    >
      {done ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      {!compact && <span>{done ? "Copied" : label}</span>}
    </button>
  );
}

/** {{variables}} are what someone has to fill in, so they are made visible. */
function PromptBody({ text }: { text: string }) {
  const parts = text.split(/(\{\{[^}]+\}\}|\[[A-Z][A-Z0-9 _-]{2,}\])/g);
  return (
    <pre className="pl-body">
      {parts.map((part, i) => (/^(\{\{|\[)/.test(part) && i % 2 === 1 ? <mark key={i}>{part}</mark> : part))}
    </pre>
  );
}

export default function PromptLibrary() {
  const { user } = useAuth();
  const canEdit = user?.role === "superadmin";
  const [params, setParams] = useSearchParams();
  const prompts = useApi<Prompt[]>("/api/prompts");
  const categories = useApi<Category[]>("/api/prompts/categories");
  const [query, setQuery] = useState("");
  const category = params.get("category") || "";
  const selectedId = params.get("prompt");

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    setParams(next, { replace: key === "category" });
  };

  const all = prompts.data || [];
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of all) m.set(p.category_slug, (m.get(p.category_slug) || 0) + 1);
    return m;
  }, [all]);
  const q = query.trim().toLowerCase();
  const rows = all.filter(
    (p) => (!category || p.category_slug === category) && (!q || `${p.prompt_name} ${p.prompt_body} ${p.category_name}`.toLowerCase().includes(q)),
  );
  const selected = all.find((p) => String(p.id) === selectedId);
  // 200+ prompts: 30 at a time, starting over when the search or category changes.
  const page = useShowMore(rows, 30, `${q}|${category}`);
  // With every category showing, rows sit under their category's name (the list is already
  // in category order); the count is the whole category's, not just the rows shown so far.
  const groups = useMemo(() => {
    const out: { slug: string; name: string; total: number; items: Prompt[] }[] = [];
    for (const p of page.visible) {
      const last = out[out.length - 1];
      if (last && last.slug === p.category_slug) last.items.push(p);
      else out.push({ slug: p.category_slug, name: p.category_name, total: rows.filter((r) => r.category_slug === p.category_slug).length, items: [p] });
    }
    return out;
  }, [page.visible, rows]);
  const open = (p: Prompt) => setParam("prompt", String(p.id));
  // A date column that says the same day on every row (a bulk import) says nothing: it shows once
  // prompts were updated on different days. Each prompt's own date is always in its detail.
  const oneDay = new Set(all.map((p) => formatDate(p.updated_at))).size <= 1;
  // Likewise an "earlier versions" column with nothing in it.
  const noVersions = all.every((p) => !Number(p.version_count));

  return (
    <>
      <PageHeader eyebrow="AI system" title="Prompt library" description="Prompts by use case. Copy one and paste it into the AI analyst." />
      <div className="pl-shell">
        <section className="panel pl-list-panel">
          <div className="pl-toolbar">
            <label className="search-control pl-search">
              <Search aria-hidden="true" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search prompts and their text…" aria-label="Search prompts" />
            </label>
            <FilterPill
              label="Category"
              value={category}
              onChange={(v) => setParam("category", v || null)}
              options={[
                { value: "", name: "All", count: all.length },
                ...(categories.data || []).filter((c) => counts.get(c.slug)).map((c) => ({ value: c.slug, name: c.name, count: counts.get(c.slug) || 0 })),
              ]}
            />
            <span className="result-count" aria-live="polite">{prompts.loading ? "Loading…" : `${rows.length} ${rows.length === 1 ? "prompt" : "prompts"}`}</span>
          </div>

          {prompts.loading && !prompts.data ? <LoadingState label="Loading prompts" /> : prompts.error ? <ErrorState message={prompts.error} onRetry={prompts.reload} /> : !rows.length ? (
            <EmptyState title={q || category ? "No matching prompts" : "No prompts available"} description={q || category ? "Try another search or category." : "Active prompts will appear here."} />
          ) : (
            // Rows, not cards: the name and a one-line preview, then tier, earlier versions and the
            // last update in aligned columns. The whole row opens the prompt; the name is the link.
            <div className={["pl-rows", oneDay && "no-date", noVersions && "no-versions"].filter(Boolean).join(" ")}>
              {groups.map((g) => (
                <section key={g.slug} className="pl-group">
                  {!category && <h3 className="pl-group-head">{g.name}<span>{g.total}</span></h3>}
                  <ul>
                    {g.items.map((p) => (
                      <li key={p.id} className={selected?.id === p.id ? "pl-row active" : "pl-row"} onClick={() => open(p)}>
                        <div className="pl-row-main">
                          <a
                            className="pl-row-name"
                            href={`/prompts?prompt=${p.id}`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); open(p); }}
                          >
                            {noDash(p.prompt_name)}
                          </a>
                          <p className="pl-row-snippet">{p.prompt_body.replace(/\s+/g, " ").slice(0, 200)}</p>
                        </div>
                        {/* Columns on wide screens; one line under the name on phones. */}
                        <span className="pl-row-meta">
                          <span className="pl-row-tier">{tierLabel(p.model_tier)}</span>
                          {!noVersions && <span className="pl-row-versions">{earlierVersions(Number(p.version_count))}</span>}
                          {!oneDay && <span className="pl-row-date">{formatDate(p.updated_at)}</span>}
                        </span>
                        <CopyButton text={p.prompt_body} compact />
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
          {!!rows.length && page.control}
        </section>

        {selected && (
          <PromptDetail
            key={selected.id}
            prompt={selected}
            canEdit={canEdit}
            onClose={() => setParam("prompt", null)}
            onSaved={(patch) => prompts.setData((list) => list?.map((x) => (x.id === selected.id ? { ...x, ...patch } : x)) ?? list)}
          />
        )}
      </div>
    </>
  );
}

function PromptDetail({
  prompt,
  canEdit,
  onClose,
  onSaved,
}: {
  prompt: Prompt;
  canEdit: boolean;
  onClose: () => void;
  onSaved: (patch: Partial<Prompt>) => void;
}) {
  const [tab, setTab] = useState<"prompt" | "history">("prompt");
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(prompt.prompt_name);
  const [body, setBody] = useState(prompt.prompt_body);
  const [tier, setTier] = useState(prompt.model_tier);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const versions = useApi<Version[]>(tab === "history" ? `/api/prompts/${prompt.id}/versions` : null);

  const dialogRef = useRef<HTMLDialogElement>(null);
  const changed = editing && (name !== prompt.prompt_name || body !== prompt.prompt_body || tier !== prompt.model_tier);
  // A real modal: focus is trapped, the page behind is inert, Esc and a click on
  // the dimmed backdrop close it (after asking, if an edit is unsaved).
  useEffect(() => {
    const el = dialogRef.current;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (el && !el.open) el.showModal();
    return () => { document.body.style.overflow = overflow; if (el?.open) el.close(); previous?.focus(); };
  }, []);
  const requestClose = () => {
    if (changed && !window.confirm("Discard your changes to this prompt?")) return;
    onClose();
  };

  async function save() {
    if (!name.trim() || !body.trim()) { setError("A prompt needs a name and some text."); return; }
    setSaving(true);
    setError("");
    try {
      await api(`/api/prompts/${prompt.id}`, { method: "PUT", body: JSON.stringify({ prompt_name: name.trim(), prompt_body: body, model_tier: tier }) });
      onSaved({ prompt_name: name.trim(), prompt_body: body, model_tier: tier, updated_at: new Date().toISOString(), version_count: Number(prompt.version_count) + 1 });
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save this prompt.");
    } finally {
      setSaving(false);
    }
  }

  const words = body.trim() ? body.trim().split(/\s+/).length : 0;

  return (
    <dialog
      ref={dialogRef}
      className="pl-modal"
      aria-label={noDash(prompt.prompt_name)}
      onCancel={(e) => { e.preventDefault(); requestClose(); }}
      onClick={(e) => {
        // Clicks on the <dialog> element itself land on the backdrop area.
        if (e.target !== e.currentTarget) return;
        const r = e.currentTarget.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) requestClose();
      }}
    >
    {/* autoFocus: the dialog opens with focus on itself, not a ringed X button. */}
    <div className="pl-detail" tabIndex={-1} autoFocus>
      <div className="pl-detail-head">
        <span className="pl-cat"><i aria-hidden="true" />{prompt.category_name}</span>
        <button type="button" className="icon-button" aria-label="Close prompt" onClick={requestClose}><X aria-hidden="true" /></button>
      </div>
      {editing ? (
        <input className="pl-name-input" value={name} onChange={(e) => setName(e.target.value)} aria-label="Prompt name" maxLength={255} />
      ) : (
        <h2 className="pl-detail-title">{noDash(prompt.prompt_name)}</h2>
      )}
      <div className="pl-detail-bar">
        <div className="pl-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "prompt"} className={tab === "prompt" ? "active" : ""} onClick={() => setTab("prompt")}>Prompt</button>
          <button type="button" role="tab" aria-selected={tab === "history"} className={tab === "history" ? "active" : ""} onClick={() => setTab("history")} disabled={editing}><History aria-hidden="true" />History</button>
        </div>
        {tab === "prompt" && !editing && (
          <div className="pl-detail-actions">
            {canEdit && <button type="button" className="button secondary" onClick={() => setEditing(true)}><Pencil aria-hidden="true" /> Edit</button>}
            <CopyButton text={prompt.prompt_body} label="Copy prompt" />
            <Link className="button" to={`/analyst?prompt_id=${prompt.id}`}><Bot aria-hidden="true" /> Use in AI analyst</Link>
          </div>
        )}
      </div>

      {tab === "prompt" ? (
        editing ? (
          <div className="pl-edit">
            <label className="pl-tier-select">Model tier
              <select value={tier} onChange={(e) => setTier(e.target.value)}>
                <option value="sonnet">Sonnet</option>
                <option value="opus">Opus</option>
              </select>
            </label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} aria-label="Prompt text" spellCheck={false} />
            <p className="pl-edit-note">{words} words. Saving keeps the current text as a version in History.</p>
            {error && <p role="alert" className="inline-notice danger">{error}</p>}
            <div className="operation-actions">
              <button type="button" className="button secondary" onClick={() => { setEditing(false); setName(prompt.prompt_name); setBody(prompt.prompt_body); setTier(prompt.model_tier); setError(""); }} disabled={saving}>Cancel</button>
              <button type="button" className="button" onClick={() => void save()} disabled={saving}>{saving ? "Saving…" : "Save prompt"}</button>
            </div>
          </div>
        ) : (
          <>
            <p className="pl-detail-meta">
              <span className={`pl-tier t-${prompt.model_tier}`}><Sparkles aria-hidden="true" />{tierLabel(prompt.model_tier)}</span>
              <span>{words} words</span>
              <span>Updated {formatDate(prompt.updated_at)}</span>
              {helpfulLine(prompt) && <span>{helpfulLine(prompt)}</span>}
            </p>
            <PromptBody text={prompt.prompt_body} />
          </>
        )
      ) : versions.loading ? <LoadingState label="Loading history" /> : versions.error ? <ErrorState message={versions.error} onRetry={versions.reload} /> : !versions.data?.length ? (
        <p className="pl-empty-history">No earlier versions. One is kept each time this prompt is edited.</p>
      ) : (
        <ol className="pl-versions">
          {versions.data.map((v) => (
            <li key={v.id}>
              <div className="pl-version-head">
                <strong>Version {v.version_number}</strong>
                <span>{formatDate(v.created_at)}</span>
                <span>{tierLabel(v.model_tier)}</span>
                <CopyButton text={v.prompt_body} compact />
              </div>
              {v.change_reason && <p className="pl-version-reason">{v.change_reason}</p>}
              <pre className="pl-body small">{v.prompt_body}</pre>
            </li>
          ))}
        </ol>
      )}
    </div>
    </dialog>
  );
}
